/* eslint-disable no-console */
// Global imports
import React, {createContext, useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {useHistory} from 'react-router-dom';

// Project imports
import {dateToShortDate} from 'ki-common/utils/dateHelpers';
import {usePrevious, useMergedState} from 'utils/customHooks';

// Local imports
import {
	fetchBookmarkById,
	fetchDefaultBookmark,
	fetchScenarios,
	fetchModel,
	fetchQuickFilters,
	fetchBorrowingBaseScenarios,
} from 'api/fundingAnalysisApi';
import {fetchPortfolioDates, getCalculatedDateInfo} from 'api/datasetDatesApi';

const useLogging = false; // Set to true to enable console logging

const defaultState = {
	_id: null,
	createdBy: null,
	createdAt: null,
	updatedAt: null,
	isDefault: false,
	isFavorite: false,
	name: '',
	datasetId: null,
	createDate: null,
	tags: [],
	settings: {
		viewType: null, // scenario|summary|pool|excess|breaches
		tableType: null,
		sortOrder: 'asc',
		sortColumn: null,
		constraintGroup: 'setup',
		statementDate: dateToShortDate(new Date()),
		dateContext: '',
		scenarioType: 'assetSnapshot',
		scenarioId: null,
		transferDate: null,
		fundingVehicleIds: ['all'],
	},
	viewColumns: {
		scenario: [],
		summary: [],
		breaches: [],
		compliance: [],
	},
};

// Main context
export const BookmarkContext = createContext();
BookmarkContext.displayName = 'Funding Bookmark Context';

// Main provider
export const BookmarkProvider = ({children}) => {
	const history = useHistory();
	const [state, setState, resetState] = useMergedState(defaultState);
	const [parent, setParent] = useState({});
	const [appliedBookmark, setAppliedBookmark] = useState(_.cloneDeep(defaultState));
	const [scenarioActive, setScenarioActive] = useState({});
	const [appliedScenario, setAppliedScenario] = useState({});
	const [model, setModel] = useState({});
	const [hasChanges, setHasChanges] = useState(false);
	const prevState = usePrevious(state);

	// Initialization variables
	const [coreSettings, setCoreSettings, resetCoreSettings] = useMergedState({
		datasetId: '',
		bookmarkId: null,
		scenarioId: null,
	});

	// UI State Items
	const [datasetDateList, setDatasetDateList] = useState([]);
	const [scenarioList, setScenarioList] = useState([]);
	const [infoRibbonMessage, setInfoRibbonMessage] = useState(null);
	const [scenarioDropdownList, setScenarioDropdownList] = useState([]);
	const [scenarioDropdownVal, setScenarioDropdownVal] = useState(null);

	useEffect(
		() => {
			if (state?._id && prevState?._id && prevState._id === state._id) {
				setHasChanges(true);
			}
		},
		[state]
	);

	const resetBookmarkContext = () => {
		useLogging && console.log('resetBookmarkContext - resetCoreSettings');
		resetCoreSettings({
			datasetId: '',
			bookmarkId: null,
			scenarioId: null,
		});
	};

	// scenarioDropdownVal Change
	useEffect(
		() => {
			useLogging && console.log('Scenario Dropdown Value Change', scenarioDropdownVal);
		},
		[scenarioDropdownVal]
	);

	const fetchScenarioQuickFilters = async ({statementDate, dateContext, tableType, viewType}) => {
		useLogging &&
			console.log(
				`fetchScenarioQuickFilters statementDate: ${statementDate}, dateContext: ${dateContext}, tableType: ${tableType}, viewType: ${viewType}`
			);

		if (!coreSettings.datasetId) return;
		if (['walkOfAssets', 'borrowingBaseBreaches', 'excessWithoutDuplication'].includes(viewType)) {
			useLogging &&
				console.log(
					'fetchScenarioQuickFilters - walkOfAssets, borrowingBaseBreaches, excessWithoutDuplication'
				);
			const bbScenarios = await fetchBorrowingBaseScenarios(coreSettings.datasetId, statementDate, dateContext);

			// For borrowingBase views add the predefined options and filter scenario list for only matches
			const tempList = [
				{
					label: 'Start of Day',
					value: 'assetSnapshot',
				},
				{
					label: 'End of Day',
					value: 'lastApproved',
				},
				// Because these scenarios include the full object we can see the lastSolvedModel,
				// this would not be true of scenarios returned in the fetch quick filter list
				{
					label: 'Hypo',
					options: bbScenarios.filter(opt => opt.lastSolvedModel.isBorrowingBase).map(opt => {
						return {
							...opt,
							label: opt.name,
							value: opt._id,
						};
					}),
				},
			];
			useLogging && console.log('fetchScenarioQuickFilters (borrowing base): ', tempList);
			setScenarioDropdownList(tempList);
			return tempList;
		} else if (tableType === 'scenario') {
			useLogging && console.log('fetchScenarioQuickFilters - tableType scenario');
			const tempList = scenarioList.map(entry => {
				const option = structuredClone(entry);
				option.label = option.name;
				option.value = option._id;
				return option;
			});
			useLogging && console.log('fetchScenarioQuickFilters (scenario): ', tempList);
			setScenarioDropdownList(tempList);
			return scenarioList;
		} else {
			useLogging && console.log('fetchScenarioQuickFilters - tableType summary');
			const {transferDates} = await fetchQuickFilters(
				coreSettings.datasetId,
				statementDate || dateToShortDate(new Date()),
				dateContext
			);

			const dateOptions = !transferDates.length
				? [
						{
							label: 'No Pending Found',
							type: 'transferDate',
							value: 'No Pending Found',
							disabled: true,
						},
				  ]
				: transferDates.map(tDate => ({
						label: tDate,
						type: 'transferDate',
						value: tDate, // actual transferDate
				  }));

			const tempList = [
				{
					label: 'Start of Day',
					value: 'assetSnapshot',
				},
				{
					label: 'End of Day',
					value: 'lastApproved',
				},
				{
					label: 'Pending',
					options: dateOptions,
				},
			];

			useLogging && console.log('fetchScenarioQuickFilters (summary): ', tempList);
			setScenarioDropdownList(tempList);
			return tempList;
		}
	};

	// If there is a passed dataset use that, otherwise use state
	const getScenarios = async datasetIdParam => {
		useLogging && console.log('getScenarios');
		const scenarios = await fetchScenarios(datasetIdParam || coreSettings.datasetId);
		useLogging && console.log('getScenarios', scenarios);
		setScenarioList(scenarios);
		return scenarios;
	};

	const getRibbonMessage = () => {
		if (!coreSettings.datasetId) return null;
		if (!['summary', 'eligibility', 'borrowingBase'].includes(state.settings.tableType)) {
			return null;
		}
		let targetDate = datasetDateList.find(dateColumn => dateColumn._id === state.settings.dateContext);
		if (!targetDate) {
			targetDate = datasetDateList.find(dateColumn => dateColumn.name === 'Latest Snapshot');
		}

		return getCalculatedDateInfo(
			targetDate._id,
			datasetDateList,
			state.settings.statementDate || dateToShortDate(new Date()),
			undefined,
			false,
			coreSettings.datasetId
		).then(res => {
			setInfoRibbonMessage(
				`Data results for ${res[0].calculatedDate} using Statement Date and ${targetDate.name} context`
			);
		});
	};

	useEffect(
		() => {
			useLogging &&
				console.log(
					'useEffect - state.settings.dateContext, state.settings.statementDate, state.settings.tableType'
				);
			getRibbonMessage();
			fetchScenarioQuickFilters(state.settings);
		},
		[state.settings.dateContext, state.settings.statementDate, state.settings.tableType]
	);

	useEffect(
		() => {
			useLogging && console.log('useEffect - state.settings.dateContext');
		},
		[state.settings.dateContext]
	);
	useEffect(
		() => {
			useLogging && console.log('useEffect - state.settings.statementDate');
		},
		[state.settings.statementDate]
	);
	useEffect(
		() => {
			useLogging && console.log('useEffect - state.settings.tableType');
		},
		[state.settings.tableType]
	);

	const applyBookmark = (newBookmark = state, newScenario = scenarioActive) => {
		useLogging && console.log('applyBookmark');
		setAppliedBookmark(_.cloneDeep(newBookmark));
		setAppliedScenario(_.cloneDeep(newScenario));
		setHasChanges(false);
	};

	// Just loads the correct bookmark into the state, another call is made to get data
	const loadBookmark = async (
		bookmarkData,
		apply = false,
		datasetDates = datasetDateList,
		scenarios = scenarioList
	) => {
		const toPrint = structuredClone(bookmarkData);
		delete toPrint.viewColumns;
		useLogging && console.log('loadBookmark', JSON.stringify(toPrint, null, 4));
		const settings = bookmarkData.settings;
		let targetScenario = scenarioActive; // Used to prevent race condition with setScenario
		let scenarioContextList = []; // Used to prevent race condition with setBorrowingBaseScenarios

		// Default statement date to current date
		if (!settings.statementDate) {
			_.set(bookmarkData, 'settings.statementDate', dateToShortDate(new Date()));
		}

		if (settings.tableType === 'scenario') {
			// If this is a scenario view and none is selected use the first one from the list
			if (_.isEmpty(settings.scenarioId)) {
				_.set(bookmarkData, 'settings.scenarioId', scenarios[0]._id);
			}

			// Fetch the associated scenario
			targetScenario = scenarios.find(s => s._id === settings.scenarioId);
			setScenarioActive(targetScenario);

			// Fetch the associated model
			// TODO check if its the same model to prevent extra calls
			let model = null;
			if (targetScenario?.fundingModelId && targetScenario.fundingModelId === 'Not Found') {
				model = await fetchModel(targetScenario.fundingModelId).catch(() => Promise.resolve(null));
			}
			setModel(model);
			_.set(bookmarkData, 'settings.isBlended', !!model?.isBlended);
		} else {
			// All non-scenario views need a date context
			if (!settings.dateContext) {
				const latestDateContext = datasetDates.find(date => date.name === 'Latest Snapshot');
				_.set(bookmarkData, 'settings.dateContext', latestDateContext._id);
			}
			scenarioContextList = await fetchScenarioQuickFilters(bookmarkData.settings);
		}

		// Check if the original scenario is still valid, default to End of Day / lastApproved
		if (settings.tableType === 'borrowingBase') {
			const options = scenarioContextList.flatMap(opt => (opt.options ? opt.options : opt));
			// Find the scenario in the list if it exists
			targetScenario = options.find(s => s._id === settings.scenarioId);
			// Fallback to first scenario in the list
			targetScenario = targetScenario || scenarioContextList[0];

			if (!targetScenario) {
				// Fallback to End of Day / lastApproved if no scenarios are found
				_.set(bookmarkData, 'settings.scenarioId', null);
				_.set(bookmarkData, 'settings.scenarioType', 'lastApproved');
			} else {
				_.set(bookmarkData, 'settings.scenarioId', targetScenario._id);
			}
		}

		useLogging && console.log('loadBookmark - bookmarkData: ', bookmarkData);
		resetState(bookmarkData);

		if (apply) {
			applyBookmark(bookmarkData, targetScenario);
		}
	};

	const loadBookmarkById = async bookmarkId => {
		useLogging && console.log('loadBookmarkById');
		const bookmarkData = await fetchBookmarkById(bookmarkId);
		loadBookmark(bookmarkData);
	};

	// If there is a passed dataset use that, otherwise use state
	const fetchDatasetDates = async datasetIdParam => {
		useLogging && console.log('fetchDatasetDates', datasetIdParam);
		const dates = await fetchPortfolioDates(datasetIdParam || coreSettings.datasetId);
		setDatasetDateList(dates);
		return dates;
	};

	const _initializeBookmark = async () => {
		const {datasetId, bookmarkId, scenarioId} = coreSettings;
		useLogging &&
			console.log(
				`_initializeBookmark datasetId:${datasetId}, bookmarkId:${bookmarkId}, scenarioId:${scenarioId}`
			);
		if (!datasetId) {
			return false; // Shortcut out since page is not loaded
		}

		// Clear all pre-existing fetch data

		const datasetDatesRes = await fetchDatasetDates(datasetId);
		let scenariosRes = await getScenarios(datasetId);

		let bookmarkData;
		if (bookmarkId) {
			//useLogging && console.log(`_initializeBookmark bookmarkId`, bookmarkId);
			bookmarkData = await fetchBookmarkById(bookmarkId);
			// Links from the scenarios list will include the bookmarkId and the scenario Id
			// otherwise leave this field alone or bookmarks saved with a scenario will not load
			// the correct scenario
			if (scenarioId) {
				_.set(bookmarkData, 'settings.scenarioId', scenarioId);
			}
		} else if (scenarioId) {
			//useLogging && console.log(`_initializeBookmark scenarioId`, scenarioId);
			bookmarkData = await fetchDefaultBookmark(datasetId, 'scenario');
			_.set(bookmarkData, 'settings.scenarioId', scenarioId);
			_.set(bookmarkData, 'settings.scenarioType', 'hypo');
		} else if (window.BORROWING_BASE_WOA_ENABLED) {
			//useLogging && console.log(`_initializeBookmark datasetId`, datasetId);
			bookmarkData = await fetchDefaultBookmark(datasetId, 'borrowingBase');
			scenariosRes = await fetchBorrowingBaseScenarios(
				coreSettings.datasetId,
				_.get(bookmarkData, 'settings.statementDate', dateToShortDate(new Date())),
				bookmarkData.settings.dateContext
			);
		} else {
			//useLogging && console.log(`_initializeBookmark datasetId`, datasetId);
			bookmarkData = await fetchDefaultBookmark(datasetId, 'summary');
		}
		if (bookmarkData) {
			await loadBookmark(bookmarkData, true, datasetDatesRes, scenariosRes);
		}
	};

	const initializeBookmark = async (datasetId, bookmarkId, scenarioId) => {
		useLogging &&
			console.log(
				`initializeBookmark datasetId:${datasetId}, bookmarkId:${bookmarkId}, scenarioId:${scenarioId}`
			);
		resetState(defaultState);
		setParent(null);
		const newSettings = {
			datasetId,
			bookmarkId,
			scenarioId,
		};
		if (datasetId !== coreSettings.datasetId) {
			useLogging && console.log('initializeBookmark - resetCoreSettings');
			resetCoreSettings(newSettings); // Fully replace
		} else {
			useLogging && console.log('initializeBookmark - setCoreSettings');
			setCoreSettings(newSettings); // Only change differences
		}
	};

	// When one of the core values changes, re-initialize the bookmark
	useEffect(
		() => {
			useLogging && console.log('useEffect - coreSettings', JSON.stringify(coreSettings, null, 4));
			if (coreSettings.datasetId) {
				useLogging && console.log('useEffect - coreSettings > triggers _initializeBookmark');
				_initializeBookmark();
			}
		},
		[coreSettings]
	);

	// Requires more than just a re-fetch of table data
	// Re-load for setting scenario, model, and isBlended
	const setScenarioId = async scenarioId => {
		useLogging && console.log('setScenarioId', scenarioId);
		const bookmarkData = _.cloneDeep(state);
		_.set(bookmarkData, 'settings.scenarioId', scenarioId);
		loadBookmark(bookmarkData);
	};

	const getPrintable = myList => {
		if (!myList || !Array.isArray(myList)) {
			return [];
		}
		return myList.map(e => {
			const {_id, label, value, status, options} = e;

			return {
				_id,
				label,
				value,
				status,
				...(options && {options: getPrintable(options)}),
			};
		});
	};

	const getScenarioDropdownValue = ({scenarioDropdownList, tableType, scenarioId, scenarioType, transferDate}) => {
		useLogging && console.log('getScenarioDropdownValue');
		useLogging &&
			console.log(`getScenarioDropdownValue - scenarioDropdownList`, getPrintable(scenarioDropdownList));
		useLogging &&
			console.log(
				`tableType: ${tableType}, scenarioId ${scenarioId}, scenarioType: ${scenarioType}, transferDate: ${transferDate}`
			);

		if (!scenarioDropdownList || !scenarioDropdownList.length > 0) {
			console.error('getScenarioDropdownValue - no options');
		}

		// Flatten the options to allow searching the Hypo options
		const options = scenarioDropdownList.flatMap(opt => (opt.options ? opt.options : opt));

		let selectedOption, newScenarioId, newScenarioType, newTransferDate;
		switch (tableType) {
			case 'scenario':
				useLogging && console.log('getScenarioDropdownValue - scenario');

				// Find the scenario by the ID
				selectedOption = options.find(opt => opt.value === scenarioId);

				// Find the scenario by the type match
				selectedOption = selectedOption ? selectedOption : options.find(opt => opt.value === 'lastApproved');

				// Fall back to first scenario
				selectedOption = selectedOption ? selectedOption : options[1];

				// Bookmark setting values
				newScenarioId = selectedOption._id ? selectedOption._id : null;
				newScenarioType = 'lastApproved';
				newTransferDate = null;

				break;
			case 'borrowingBase':
				useLogging && console.log('getScenarioDropdownValue - borrowingBase');

				if (scenarioId) {
					// Find the scenario by the ID
					selectedOption = options.find(opt => opt.value === scenarioId);
				} else {
					// Find the scenario by the type match
					selectedOption = options.find(opt => opt.value === scenarioType);
				}

				// Fall back to the first Hypo scenario
				selectedOption = selectedOption ? selectedOption : options.find(opt => opt?.status === 'hypo');

				// Fall back to the End of Day scenario
				selectedOption = selectedOption ? selectedOption : options.find(opt => opt.value === 'lastApproved');

				// Bookmark setting values
				newScenarioId = selectedOption._id ? selectedOption._id : null;
				newScenarioType = selectedOption.status === 'hypo' ? 'hypo' : 'lastApproved';
				newTransferDate = null;

				break;
			case 'summary':
				{
					useLogging && console.log('getScenarioDropdownValue - summary');

					const pending = scenarioDropdownList.find(sc => sc.label === 'Pending');
					if (transferDate && !_.isEmpty(pending)) {
						selectedOption = pending.options.find(opt => opt.value === transferDate);

						// Bookmark setting values
						newTransferDate = transferDate;
						newScenarioType = 'lastCommitted';
						newScenarioId = null;
					}

					if (!selectedOption) {
						// Find matching of Start of Day / End of Day
						selectedOption = options.find(sc => sc.value === scenarioType);

						// Fall back to End of Day scenario
						selectedOption = selectedOption
							? selectedOption
							: options.find(opt => opt.value === 'lastApproved');

						// Bookmark setting values
						newTransferDate = null;
						newScenarioType = selectedOption.value;
						newScenarioId = null;
					}
				}

				break;
			default:
				console.error('getScenarioDropdownValue - default');
				break;
		}

		useLogging && console.log('Actual scenario', JSON.stringify(selectedOption, null, 2));
		useLogging &&
			console.log(
				`scenarioId: ${newScenarioId}, scenarioType ${newScenarioType}, transferDate: ${newTransferDate}`
			);

		return {
			selectedOption,
			scenarioId: newScenarioId,
			scenarioType: newScenarioType,
			transferDate: newTransferDate,
		};
	};

	// If one of the three main scenario fields changes update the dropdown value
	useEffect(
		() => {
			if (!scenarioDropdownList.length > 0) {
				useLogging && console.log('ue2 - no scenario return');
				return;
			}
			if (!state.settings.tableType) {
				useLogging && console.log('ue2 - no tabletype return');
				return;
			}
			useLogging && console.log(`useEffect scenarioType: ${state.settings.scenarioType}`);
			const {selectedOption} = getScenarioDropdownValue({
				scenarioDropdownList,
				tableType: state.settings.tableType,
				scenarioId: state.settings.scenarioId,
				scenarioType: state.settings.scenarioType,
				transferDate: state.settings.transferDate,
			});
			useLogging && console.log('useEffect(scenarioType, scenarioId, transferDate', selectedOption);
			setScenarioDropdownVal(selectedOption);
		},
		[state.settings.scenarioType, state.settings.scenarioId, state.settings.transferDate, scenarioDropdownList]
	);

	// If the scenario list changed then find the default value and update the main scenario fields
	useEffect(
		() => {
			if (!scenarioDropdownList.length > 0) {
				useLogging && console.log('ue2 - no scenario return');
				return;
			}
			if (!state.settings.tableType) {
				useLogging && console.log('ue2 - no tabletype return');
				return;
			}
			useLogging && console.log('useEffect scenarioDropdownList:', scenarioDropdownList);
			useLogging && console.log('useEffect scenarioDropdownList: state.settings', state.settings);
			const {selectedOption, scenarioId, scenarioType, transferDate} = getScenarioDropdownValue({
				scenarioDropdownList,
				tableType: state.settings.tableType,
				scenarioId: state.settings.scenarioId,
				scenarioType: state.settings.scenarioType,
				transferDate: state.settings.transferDate,
			});
			setState({
				setting: {
					scenarioId,
					scenarioType,
					transferDate,
				},
			});
			setScenarioDropdownVal(selectedOption);
		},
		[scenarioDropdownList]
	);

	// Requires more than just a re-fetch of table data
	const setViewType = async viewType => {
		useLogging && console.log('setViewType', viewType);
		setParent(null);
		const bookmarkData = _.cloneDeep(state);
		const latestSnapshotContext = datasetDateList.find(date => date.name === 'Latest Snapshot');

		const currentTableType = _.get(bookmarkData, 'settings.tableType', null);

		// Handle if the viewType did not change and is borrowingBase
		if (currentTableType === 'borrowingBase' && viewType === 'borrowingBase') {
			return false;
		}

		// Handle the root of the viewType change
		if (currentTableType !== 'borrowingBase' && viewType === 'borrowingBase') {
			// Going from borrowing base type to any other type
			const bookmarkData = await fetchDefaultBookmark(coreSettings.datasetId, 'borrowingBase');
			history.push(`/fundingAnalysis/${bookmarkData.datasetId}/bookmark/${bookmarkData._id}`);
			return false;
		}
		if (currentTableType === 'borrowingBase' && viewType !== 'borrowingBase') {
			// Going from any other type to borrowing base type
			const bookmarkData = await fetchDefaultBookmark(coreSettings.datasetId, viewType);
			history.push(`/fundingAnalysis/${bookmarkData.datasetId}/bookmark/${bookmarkData._id}`);
			return false;
		}

		// Switching between non-borrowing base types
		_.set(bookmarkData, 'settings.transferDate', null);
		_.set(bookmarkData, 'settings.viewType', viewType);
		_.set(bookmarkData, 'settings.tableType', viewType);
		_.set(bookmarkData, 'settings.dateContext', latestSnapshotContext._id);

		switch (viewType) {
			case 'summary':
				_.set(bookmarkData, 'settings.scenarioType', 'lastApproved');
				_.set(bookmarkData, 'settings.scenarioId', '');
				_.set(bookmarkData, 'settings.constraintGroup', 'setup');
				break;
			case 'scenario':
				_.set(bookmarkData, 'settings.scenarioId', scenarioList[0]._id);
				_.set(bookmarkData, 'settings.scenarioType', 'hypo');
				_.set(bookmarkData, 'settings.statementDate', null);
				_.set(bookmarkData, 'settings.constraintGroup', 'setup');
				// isBlended set in loadBookmark method
				break;
			default:
				break;
		}

		loadBookmark(bookmarkData);
	};

	// Start - Drilldown Logic
	// Save a parent view of the current state, then modify the current view to add
	// required settings for the drilldown views.
	// Borrowing Base Drilldowns do not use the fvId field
	const loadDrillDown = (type, fvId = null) => {
		const newParent = _.cloneDeep(state);
		setParent(newParent);

		const bookmarkData = _.cloneDeep(state);
		_.set(bookmarkData, 'settings.viewType', type);
		if (fvId) {
			_.set(bookmarkData, 'settings.fundingVehicleIds', [fvId]);
		}
		loadBookmark(bookmarkData, true);
	};

	const restoreParent = () => {
		const bookmarkData = _.cloneDeep(parent);
		setParent(null);
		loadBookmark(bookmarkData, true);
	};
	// End - Drilldown Locig

	return (
		<BookmarkContext.Provider
			displayName="Funding Bookmark Provider"
			value={{
				bookmark: state,
				scenario: scenarioActive,
				model,
				setBookmark: setState,
				loadBookmark,
				loadBookmarkById,
				initializeBookmark,
				setScenarioId,
				setViewType,
				loadDrillDown,
				parent,
				restoreParent,
				appliedBookmark,
				applyBookmark,
				appliedScenario,
				ui: {
					infoRibbonMessage,
					datasetDateList,
					scenarioList,
					scenarioDropdownList,
					scenarioDropdownVal,
				},
				hasChanges,
				resetBookmarkContext,
			}}
		>
			{children}
		</BookmarkContext.Provider>
	);
};

BookmarkProvider.propTypes = {
	children: PropTypes.node.isRequired,
};

BookmarkProvider.defaultProps = {};
